import { getFocusabledIn } from "focus-lock";
import { filter, map } from "lodash";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";

import KeyConstants from "../../constants/keyConstants";
import { PropTypeShapes } from "../../types";

/**
 * This is my implementation of a FocusLock
 * based only on tabbing, because if the user
 * clicks outside a popover, we actually
 * want to allow the focus change.
 *
 * Reports back when focus is lost, so that the popover
 * can close itself
 */

const getFocusableChildNodes = el => {
  // Note: getFocusableIn() returns an array of objects with:
  // { node, index, guard }
  // We only care about the node
  const arr = getFocusabledIn(el);
  return filter(
    map(arr, a => a.node),
    n => n != el
  );
};

class TabLock extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this._domNode = null;
    this._hasFocus = null;
    this.onKeyDown = this.onKeyDown.bind(this);
    this.moveFocus = this.moveFocus.bind(this);
    this.setFocus = this.setFocus.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onBlur = this.onBlur.bind(this);
  }

  componentDidMount() {
    if (this.props.autoFocus && !this._hasFocus) {
      this._domNode.focus();
    }
  }

  onFocus(_e) {
    this._hasFocus = true;
  }

  onBlur(_e) {
    this._hasFocus = false;
  }

  onKeyDown(e) {
    if (e.key === KeyConstants.Tab) {
      // If the user hit the TAB key, then we want to take control of moving
      // between different cells
      const change = e.shiftKey ? -1 : 1;
      if (this.moveFocus(e.target, change)) {
        e.stopPropagation();
        e.preventDefault();
      }
    }
  }

  setFocus(index) {
    const nodes = getFocusableChildNodes(this._domNode);
    if (nodes.length === 0) {
      return;
    }
    let focusIndex = index;
    if (focusIndex < 0) {
      focusIndex = 0;
    }
    if (focusIndex >= nodes.length) {
      focusIndex = nodes.length - 1;
    }
    nodes[focusIndex].focus();
  }

  moveFocus(target, change) {
    const nodes = getFocusableChildNodes(this._domNode);
    if (nodes.length === 0) {
      return false;
    }
    // Find the current index
    // Will be -1 if there is no focus
    const currIndex = nodes.indexOf(target);
    // Add the change we want to the currIndex
    let nextIndex = currIndex + change;
    // If we have exceeded nodes.length, cycle back to start
    if (nextIndex >= nodes.length) {
      nextIndex = 0;
    } else if (nextIndex < 0) {
      // Otherwise, if we have gone before the beginning, go to the end
      nextIndex = nodes.length - 1;
    }
    nodes[nextIndex].focus();
    return true;
  }

  render() {
    return (
      <div
        className="tabLock"
        tabIndex={0}
        ref={node => (this._domNode = node)}
        onKeyDown={this.onKeyDown}
        onFocus={this.onFocus}
        onBlur={this.onBlur}
        style={{ outline: "none" }}
      >
        {this.props.children}
      </div>
    );
  }
}

TabLock.propTypes = {
  children: PropTypeShapes.elementOrArray.isRequired,
  autoFocus: PropTypes.bool
};

export default TabLock;
